import { createJSONStorage, persist, PersistOptions, PersistStorage, StateStorage } from 'zustand/middleware';

import { DEFAULT_EXPIRY_TIME } from '@src/constants';

const localStorageAdapter = {
	getItem: (name: string): string | null => localStorage.getItem(name),
	setItem: (name: string, value: string): void => localStorage.setItem(name, value),
	removeItem: (name: string): void => localStorage.removeItem(name),
};

export const createSafeLocalStorage = () => createJSONStorage(() => localStorageAdapter);

/**
 * Enhances the given storage with expiry handling for a specific store name.
 */
export const createExpiryStorage = (
	storage,
	storeName: string,
	expiryTimeMs: number = DEFAULT_EXPIRY_TIME
): StateStorage => {
	const lastActivityKey = `${storeName}__last_activity`;

	const updateLastActivity = () => {
		storage.setItem(lastActivityKey, JSON.stringify(Date.now()));
	};

	const hasExpired = (): boolean => {
		try {
			const last = storage.getItem(lastActivityKey);
			if (!last) return false;
			return Date.now() - JSON.parse(last.toString()) > expiryTimeMs;
		} catch {
			return false;
		}
	};

	const clearIfExpired = () => {
		if (hasExpired()) {
			try {
				Object.keys(localStorage)
					.filter((key) => key.startsWith(storeName))
					.forEach((key) => storage.removeItem(key));
			} catch (error) {
				console.error(`[persistWithExpiry] Failed to clear storage:`, error);
			}
		}
		updateLastActivity();
	};

	return {
		getItem: (name) => {
			clearIfExpired();
			return storage.getItem(name);
		},
		setItem: (name, value) => {
			clearIfExpired();
			storage.setItem(name, value);
		},
		removeItem: (name) => {
			updateLastActivity();
			storage.removeItem(name);
		},
	};
};

export interface PersistOptionsWithExpiry<T> extends PersistOptions<T> {
	expiryTimeMs?: number;
}

/**
 * Wrapper around Zustand's `persist` that adds expiry support.
 */
export function persistWithExpiry<T>(
	config: (set: any, get: any, store: any) => T,
	options: PersistOptionsWithExpiry<T>
) {
	const { expiryTimeMs = DEFAULT_EXPIRY_TIME, name, ...restOptions } = options;

	if (!name) {
		throw new Error('[persistWithExpiry] "name" is required in options');
	}

	const safeStorage = createSafeLocalStorage();
	const wrappedStorage = createExpiryStorage(safeStorage, name, expiryTimeMs);

	return persist(config, {
		name,
		storage: wrappedStorage as unknown as PersistStorage<any>,
		...restOptions,
	});
}
